import { useState, useEffect, useCallback, useRef } from 'react';
import type { Entitlements, UpgradeHint } from '@snap-platform/shared';
import { trackConversion } from '../services/conversionTracking.js';
import {
  selectEligibleHint,
  recordHintShown,
  recordHintDismissed,
} from '../conversion/upgradeHintEngine.js';

/** @deprecated Use UpgradeHint['type'] instead */
export type TriggerContext = 'quota_reached' | 'quota_warning' | 'milestone' | 'feature_gate';

export function useUpgradePrompt(entitlements: Entitlements | null) {
  const [shouldShow, setShouldShow] = useState(false);
  const [hint, setHint] = useState<UpgradeHint | null>(null);
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (hasShownRef.current) return;
    if (!entitlements || entitlements.tier !== 'free') return;
    if (!entitlements.upgradeHints || entitlements.upgradeHints.length === 0) return;

    const selected = selectEligibleHint(entitlements.upgradeHints);
    if (selected) {
      hasShownRef.current = true;
      setHint(selected);
      setShouldShow(true);
      recordHintShown(selected);
      trackConversion('revenue_hint_shown', { hint_type: selected.type, priority: selected.priority });
    }
  }, [entitlements]);

  const dismiss = useCallback(() => {
    setShouldShow(false);
    recordHintDismissed();
    trackConversion('revenue_hint_dismissed', { hint_type: hint?.type });
    setHint(null);
  }, [hint]);

  const accept = useCallback(() => {
    setShouldShow(false);
    trackConversion('revenue_hint_clicked', { hint_type: hint?.type, action: hint?.action });
    setHint(null);
  }, [hint]);

  const triggerFeatureGate = useCallback((featureName: string) => {
    const syntheticHint: UpgradeHint = {
      type: 'feature_gate',
      message: `Unlock ${featureName} and other Pro features.`,
      priority: 'medium',
    };
    const selected = selectEligibleHint([syntheticHint]);
    if (selected) {
      setHint(selected);
      setShouldShow(true);
      recordHintShown(selected);
      trackConversion('revenue_hint_shown', { hint_type: 'feature_gate', feature: featureName });
    }
  }, []);

  return { shouldShow, hint, dismiss, accept, triggerFeatureGate };
}
